import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationSubscriptionService } from './organization-subscription.service';
import {
  ManageSubscriptionButtonsActions,
  OrganizationSubscriptionEffectActions,
} from './organization-subscription.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { PopupNotificationEffectCreatorService } from 'src/app/shared/services/effect-creators/popup-notification-effect-creator.service';
import { PopupNotificationType } from 'studio-components';
import { WINDOW } from 'src/app/shared/injection-tokens/window.injection-token';
import * as Sentry from '@sentry/browser';
import { AccountTeamPageActions } from '../account-team/account-team.actions';

@Injectable()
export class OrganizationSubscriptionEffects {
  getSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OrganizationSubscriptionEffectActions.loadSubscription,
        AccountTeamPageActions.pageOpened,
      ),
      switchMap(() =>
        this.subscriptionService.getSubscription().pipe(
          map((subscription) =>
            OrganizationSubscriptionEffectActions.loadSubscriptionSucceeded({ subscription }),
          ),
          catchError(({ error: responseBody }: HttpErrorResponse) =>
            of(
              OrganizationSubscriptionEffectActions.loadSubscriptionErrored({
                error: responseBody.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  generateBillingPortalLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManageSubscriptionButtonsActions.clicked),
      switchMap(({ linkType }) =>
        this.subscriptionService.generatePortalLink(linkType).pipe(
          map((link) =>
            OrganizationSubscriptionEffectActions.generateBillingPortalLinkSucceeded({
              link,
            }),
          ),
          catchError(({ error: responseBody }: HttpErrorResponse) =>
            of(
              OrganizationSubscriptionEffectActions.generateBillingPortalLinkErrored({
                error: responseBody.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  generateBillingPortalPromoLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManageSubscriptionButtonsActions.promo),
      switchMap(({ couponId, priceId }) =>
        this.subscriptionService.generatePortalPromoLink(couponId, priceId).pipe(
          map((link) =>
            OrganizationSubscriptionEffectActions.generateBillingPortalLinkSucceeded({
              link,
            }),
          ),
          catchError(({ error: responseBody }: HttpErrorResponse) =>
            of(
              OrganizationSubscriptionEffectActions.generateBillingPortalLinkErrored({
                error: responseBody.error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  openBillingPortal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationSubscriptionEffectActions.generateBillingPortalLinkSucceeded),
      tap(({ link }) => {
        this.window.location.href = link.url;
      }),
      map(({ link }) => OrganizationSubscriptionEffectActions.openBillingPortalSucceeded({ link })),
    ),
  );

  displayOpenBillingPortalError$ = this.popupNotificationEffectCreatorService.createEffect({
    initiator: OrganizationSubscriptionEffectActions.generateBillingPortalLinkErrored,
    notificationType: PopupNotificationType.Error,
    persistent: true,
    title: () => `Unable to open billing portal.`,
    message: ({ error }) => `${error.message} (${error.name})`,
  });

  /**
   * Track when the UI has failed to block an unauthorized user
   * from clicking the manage subscription button.
   */
  reportOpenBillingPortalError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganizationSubscriptionEffectActions.generateBillingPortalLinkErrored),
        tap(({ error: entityServiceError }) => {
          const error = new Error();
          error.message = `${entityServiceError.name}: ${entityServiceError.message}`;
          error.name = 'OpenBillingPortalError';
          Sentry.captureException(error);
        }),
      ),
    { dispatch: false },
  );

  constructor(
    @Inject(WINDOW) private window: Window,
    private actions$: Actions,
    private subscriptionService: OrganizationSubscriptionService,
    private popupNotificationEffectCreatorService: PopupNotificationEffectCreatorService,
  ) {}
}
